import {
  EventNotificationFrequencies,
  Waterbody,
  WaterbodyEventNotifications,
} from '@omniafishing/core';
import { Modal } from 'antd';
import { FormInstance } from 'antd/lib/form';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isDone, isPending } from '../../constants/loading_state';
import { usePrevious } from '../../hooks/use_previous';
import { WebAnalytics } from '../../lib/web_analytics';
import { FlashMessageActions } from '../../redux/flash_message';
import {
  favoriteWaterbody,
  getEventNotificationLoadingState,
  getFavoriteLoadingState,
  updateWaterbodyNotifications,
} from '../../redux/waterbody';
import { OmniaButton } from '../omnia_button/omnia_button';
import { WaterbodyNotificationsForm } from './waterbody_notifications_form';

export interface WaterbodyNotificationsModalProps {
  waterbody: Waterbody;
  event_notifications: WaterbodyEventNotifications;
  favorite: boolean;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  hasNoNotifications?: boolean;
}

export const modalFooterStyles = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
};

export const WaterbodyNotificationsModal = (props: WaterbodyNotificationsModalProps) => {
  const { waterbody, open, onClose, favorite, event_notifications, onSuccess, hasNoNotifications } =
    props;
  const isFavorite = favorite;
  const dispatch = useDispatch();
  const formRef = React.createRef<FormInstance>();
  const eventNotificationLoadingState = useSelector(getEventNotificationLoadingState);
  const prevEventNotificationLoadingState = usePrevious(eventNotificationLoadingState);
  const favoriteLoadingState = useSelector(getFavoriteLoadingState);
  const prevFavoriteLoadingState = usePrevious(favoriteLoadingState);

  // update notifications success
  useEffect(() => {
    if (isPending(prevEventNotificationLoadingState) && isDone(eventNotificationLoadingState)) {
      dispatch(
        FlashMessageActions.FLASH_MESSAGE_SET({
          header: 'Notifications Updated',
          subheader: `Your notification settings for ${waterbody.primary_name}} have been updated`,
        })
      );
      onSuccess?.();
      onClose();
    }
  }, [eventNotificationLoadingState]);

  // favorite success
  useEffect(() => {
    if (isPending(prevFavoriteLoadingState) && isDone(favoriteLoadingState)) {
      onSuccess?.();
      onClose();
      WebAnalytics.itemFavorited('waterbody', waterbody.url_slug);
    }
  }, [favoriteLoadingState]);

  const onOk = async () => {
    const form = formRef.current;
    interface Values {
      new_fishing_report: boolean;
      new_article: boolean;
      email: string;
      frequency: EventNotificationFrequencies;
    }
    try {
      const { new_fishing_report, email, new_article, frequency }: Values =
        await form.validateFields();
      if (isFavorite) {
        await dispatch(
          updateWaterbodyNotifications(
            waterbody.url_slug,
            {
              new_fishing_report,
              new_article,
              frequency,
            },
            email
          )
        );
        if (hasNoNotifications) {
          WebAnalytics.followUpEnabled(email);
        }
      } else {
        dispatch(
          favoriteWaterbody(
            waterbody.url_slug,
            {
              new_fishing_report,
              new_article,
              frequency,
            },
            email
          )
        );
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Modal
      open={open}
      title={isFavorite ? 'Update Your Notifications' : 'Set Your Notifications'}
      onOk={onOk}
      onCancel={onClose}
      destroyOnClose
      footer={null}
    >
      <WaterbodyNotificationsForm
        favorite={favorite}
        waterbody={waterbody}
        event_notifications={event_notifications}
        formRef={formRef}
      />
      <div style={modalFooterStyles}>
        <OmniaButton
          kind="tertiary"
          size="lg"
          fontSize={14}
          isDisabled={
            isFavorite ? isPending(eventNotificationLoadingState) : isPending(favoriteLoadingState)
          }
          onPress={() => {
            onClose();
          }}
        >
          Cancel
        </OmniaButton>
        <OmniaButton
          kind="secondary"
          size="lg"
          fontSize={14}
          onPress={onOk}
          loading={
            isFavorite ? isPending(eventNotificationLoadingState) : isPending(favoriteLoadingState)
          }
        >
          {isFavorite ? 'Update' : 'Follow'}
        </OmniaButton>
      </div>
    </Modal>
  );
};
